// ═══ CONSTRUCTION CMS — Main App ═══
// This wraps the CMS with authentication.
//
// Only what the FIRST screen needs is loaded up front: this file, the login page and sign-in. The CMS itself (the
// large part) and the three public pages are separate files loaded on demand (utils/lazyWithRetry.js), and while
// someone is on the login screen the CMS is fetched in the background so signing in does not wait for it.

import { Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';

const CMS = lazyWithRetry(() => import('./CMS'));
const ClientPortal = lazyWithRetry(() => import('./pages/ClientPortal'));
const ToolboxSign = lazyWithRetry(() => import('./pages/ToolboxSign'));
const PqqForm = lazyWithRetry(() => import('./pages/PqqForm'));
const ShortRedirect = lazyWithRetry(() => import('./pages/ShortRedirect'));

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0B1120', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🏗️</div>
        <div style={{ color: '#E5A822', fontSize: 16, fontWeight: 600 }}>Loading CMS...</div>
      </div>
    </div>
  );
}

// Run `fn` when the browser is idle (or soon after, where there is no requestIdleCallback).
const whenIdle = (fn) => {
  if (typeof window.requestIdleCallback === 'function') { const id = window.requestIdleCallback(fn, { timeout: 3000 }); return () => window.cancelIdleCallback(id); }
  const id = window.setTimeout(fn, 1500); return () => window.clearTimeout(id);
};

function AppContent() {
  const { user, loading } = useAuth();

  // The login page is showing, so the CMS is the next thing this person needs: start downloading it now.
  // A failure here is ignored; the real load (with its retry and error screen) happens after sign-in.
  useEffect(() => {
    if (loading || user) return undefined;
    return whenIdle(() => { import('./CMS').catch(() => {}); });
  }, [loading, user]);

  if (loading) return <LoadingScreen />;

  // If no backend connected yet, show CMS directly (dev mode)
  // In production, remove this and require login

  return user ? <CMS /> : <LoginPage />;
}

export default function App() {
  // /portal is the CLIENT-facing app: its own login, its own token
  // (cms_client_token), and it only ever calls /api/v1/client-portal/*.
  // It must not mount AuthProvider — that would run the staff /me check.
  const path = window.location.pathname.replace(/\/+$/, '');

  // Everything that is loaded on demand sits inside one Suspense (the loading screen) and one error boundary.
  const shell = (node) => (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{node}</Suspense>
    </ChunkErrorBoundary>
  );

  if (path === '/portal') {
    return shell(<ClientPortal />);
  }

  // /sign is the ATTENDEE-facing toolbox talk sign-off, reached from a link
  // sent to their phone. The token in the URL is the only authorisation, so
  // it must not mount AuthProvider — an operative has no CMS login.
  if (path === '/sign') {
    return shell(<ToolboxSign />);
  }

  // /pqq is the SUBCONTRACTOR-facing questionnaire, reached from a link texted
  // to their phone. Same rule as /sign: the token is the only authorisation, so
  // it must not mount AuthProvider — a subcontractor has no CMS login.
  if (path === '/pqq') {
    return shell(<PqqForm />);
  }

  // /s is the SHORT-LINK redirector (/s/:code).
  // Single-use short URLs for PQQ and toolbox talk signing.
  if (path.startsWith('/s/')) {
    return shell(<ShortRedirect code={path.slice(3)} />);
  }

  return shell(
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
